import React, { useEffect, useState } from "react";
import axios from "../utils/axiosConfig";
import { useParams } from "react-router-dom";
import {
  Container, Paper, Typography, TextField, Grid, Button, Divider
} from "@mui/material";
import { useNavigate } from 'react-router-dom';

import FormattableTextField from '../components/FormattableTextField';

import { handleApiError } from "../utils/apiErrorHandler";

interface RegistrationFormData {
  totalFeeElite: number;
  totalFeeBasic: number;
  totalPenaltyFeeElite: number;
  totalPenaltyFeeBasic: number;
  totalTShirtFee: number;
  totalCateringFee: number;
  totalOvernightFee: number;
  totalAmount: number;
}

interface ConfirmationFormData {
  fees: {
    totalFeeElite: number;
    totalFeeBasic: number;
    totalPenaltyFeeElite: number;
    totalPenaltyFeeBasic: number;
    totalTShirtFee: number;
    totalCateringFee: number;
    totalOvernightFee: number;
    gutschrift: { amount: number; description: string };
    totalAmount: number;
  };
  customNotes: string;
}

interface CompetitionData {
  _id: string;
  name: string;
  paymentInstructions?: string;
  additionalNotes?: string;
}

interface ClubData {
  _id: string;
  name: string;
  address: {street: string; postalCode: string; city: string;};
  contactPerson: { 
    _id: string;
    name: string; 
    email: string;
  };
}

const RegistrationConfirmationForm: React.FC = () => {
  const { competitionId, clubId } = useParams();
  const [formData, setFormData] = useState<ConfirmationFormData | null>(null);
  const [clubData, setClubData] = useState<ClubData>();
  const [competitionData, setCompetitionData] = useState<CompetitionData>();
  const [loading, setLoading] = useState(true);  
  const navigate = useNavigate();

  const handleChangePenalty = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof ConfirmationFormData["fees"]) => {
    /*setFormData({
      ...formData!,
      fees: { ...formData!.fees, [field]: parseFloat(e.target.value) || 0 }
    });*/
    if (!formData) return;
  
    // Create updated fees object with the changed field
    const updatedFees = { 
      ...formData.fees, 
      [field]: parseFloat(e.target.value) || 0 
    };
    
    // Calculate totalAmount using the updated fees (Gutschrift wird abgezogen)
    const totalAmount =
      updatedFees.totalFeeElite +
      updatedFees.totalFeeBasic +
      updatedFees.totalPenaltyFeeElite +
      updatedFees.totalPenaltyFeeBasic +
      updatedFees.totalTShirtFee +
      updatedFees.totalCateringFee +
      updatedFees.totalOvernightFee -
      (updatedFees.gutschrift?.amount || 0);
    
    // Update state with all changes including totalAmount
    setFormData({
      ...formData,
      fees: { 
        ...updatedFees, 
        totalAmount 
      }
    });
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [registrationResponse, competitionResponse, clubResponse] = await Promise.all([
            axios.get(`/api/competition-registrations/${competitionId}/${clubId}/latest`),
            axios.get(`/api/competitions/${competitionId}`),
            axios.get(`/api/clubs/${clubId}`),
          ]);
          const registrationResponseData: RegistrationFormData = { ...registrationResponse.data };

          // Bestehende Bestätigung laden (für Gutschrift und Strafgebühren)
          let existingGutschrift = { amount: 0, description: "" };
          let existingCustomNotes = "";
          let existingPenaltyElite = registrationResponseData.totalPenaltyFeeElite;
          let existingPenaltyBasic = registrationResponseData.totalPenaltyFeeBasic;
          try {
            const confirmationResponse = await axios.get(`/api/registration-confirmations/${competitionId}/${clubId}`);
            if (confirmationResponse.data?.fees?.gutschrift) {
              existingGutschrift = confirmationResponse.data.fees.gutschrift;
            }
            if (confirmationResponse.data?.fees?.totalPenaltyFeeElite !== undefined) {
              existingPenaltyElite = confirmationResponse.data.fees.totalPenaltyFeeElite;
            }
            if (confirmationResponse.data?.fees?.totalPenaltyFeeBasic !== undefined) {
              existingPenaltyBasic = confirmationResponse.data.fees.totalPenaltyFeeBasic;
            }
            existingCustomNotes = confirmationResponse.data?.customNotes || "";
          } catch {
            // Keine bestehende Bestätigung – Standardwerte bleiben
          }

          const feesWithGutschrift = {
            ...registrationResponseData,
            totalPenaltyFeeElite: existingPenaltyElite,
            totalPenaltyFeeBasic: existingPenaltyBasic,
            gutschrift: existingGutschrift,
          };
          const totalAmount =
            feesWithGutschrift.totalFeeElite +
            feesWithGutschrift.totalFeeBasic +
            feesWithGutschrift.totalPenaltyFeeElite +
            feesWithGutschrift.totalPenaltyFeeBasic +
            feesWithGutschrift.totalTShirtFee +
            feesWithGutschrift.totalCateringFee +
            feesWithGutschrift.totalOvernightFee -
            existingGutschrift.amount;

          const formDatePrep: ConfirmationFormData = {
            fees: { ...feesWithGutschrift, totalAmount },
            customNotes: existingCustomNotes
          };

          setFormData(formDatePrep);
          setClubData(clubResponse.data);
          setCompetitionData(competitionResponse.data);
      } catch (error) {
        handleApiError(error, "Fehler beim Laden der Bestätigungsdaten.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [competitionId, clubId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof ConfirmationFormData["fees"]) => {
    if (!formData) return;
    setFormData({
      ...formData,
      fees: { ...formData.fees, [field]: parseFloat(e.target.value) || 0 }
    });
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof ConfirmationFormData) => {
    if (!formData) return;
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleGutschriftChange = (field: "amount" | "description", value: string) => {
    if (!formData) return;
    const updatedGutschrift = {
      ...formData.fees.gutschrift,
      [field]: field === "amount" ? (parseFloat(value) || 0) : value
    };
    const totalAmount =
      formData.fees.totalFeeElite +
      formData.fees.totalFeeBasic +
      formData.fees.totalPenaltyFeeElite +
      formData.fees.totalPenaltyFeeBasic +
      formData.fees.totalTShirtFee +
      formData.fees.totalCateringFee +
      formData.fees.totalOvernightFee -
      (field === "amount" ? (parseFloat(value) || 0) : formData.fees.gutschrift.amount);
    setFormData({
      ...formData,
      fees: { ...formData.fees, gutschrift: updatedGutschrift, totalAmount }
    });
  };

  const handleSave = async () => {
    try {
      await axios.post(`/api/registration-confirmations`, formData);      
    } catch (error) {
      handleApiError(error, "Fehler beim Speichern der Meldebestätigung.");
      alert("Fehler beim Speichern der Meldebestätigung.");
    }
  };  

  const handleSaveAndPDF = async () => {
    try {
      const confirmation = await axios.post(`/api/registration-confirmations`, formData);
      const confirmationId= confirmation.data._id.toString();           
    
      // PDF auf dem Server erzeugen, ohne es herunterzuladen
      await axios.post(`/api/registration-confirmations/${confirmationId}/generate-pdf`);
    
      // Zur Bestätigungsseite navigieren
      navigate(`/orga/competition/${competitionId}/confirmation-registration/${confirmationId}`);
    } catch (error) {
      handleApiError(error, "Fehler beim Speichern und PDF Erstellen.");
      alert("Fehler beim Speichern und PDF Erstellung der Meldebestätigung.");
    }
  };

  // Helper function to safely access contactPerson data
  const getContactPersonInfo = () => {
    if (clubData?.contactPerson) {
      // Check if we have name and email directly
      if (clubData.contactPerson.name && clubData.contactPerson.email) {
        return {
          name: clubData.contactPerson.name,
          email: clubData.contactPerson.email
        };
      }
      
      // If contactPerson is just an ObjectId or different structure
      return {
        name: "Kontaktperson nicht gefunden",
        email: "Keine E-Mail verfügbar"
      };
    }
    return {
      name: "Keine Kontaktperson zugeordnet",
      email: "Keine E-Mail verfügbar"
    };
  };

  if (loading) {
    return <Typography>Lade Daten...</Typography>;
  }

  const contactInfo = getContactPersonInfo();

  return (  
    <Container maxWidth="md">
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5">📄 Meldebestätigung erstellen</Typography>
        
        {/* Vereinsdaten */}
        <Typography variant="h6" sx={{ mt: 2 }}>🏠 Vereinsinformationen</Typography>
        <Typography><strong>Verein:</strong> {clubData?.name}</Typography>
        <Typography><strong>Adresse:</strong> {clubData?.address.street}, {clubData?.address.postalCode} {clubData?.address.city}</Typography>
        <Typography><strong>Ansprechpartner:</strong> {contactInfo.name}</Typography>
        <Typography><strong>Email:</strong> {contactInfo.email}</Typography>

        <Divider sx={{ my: 2 }} />

        {/* Gebührenübersicht */}
        <Typography variant="h6">💰 Gebühren</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}><Typography>Elite-Meldegebühr:</Typography></Grid>
          <Grid item xs={6}><Typography>{formData?.fees.totalFeeElite} € </Typography></Grid>
          
          <Grid item xs={6}><Typography>Basis-Meldegebühr:</Typography></Grid>
          <Grid item xs={6}><Typography>{formData?.fees.totalFeeBasic} €</Typography></Grid>

          <Grid item xs={6}><Typography>Elite-Strafgebühr:</Typography></Grid>
          <Grid item xs={6}>
            <TextField fullWidth value={formData?.fees.totalPenaltyFeeElite} onChange={(e) => handleChangePenalty(e, "totalPenaltyFeeElite")} />
          </Grid>

          <Grid item xs={6}><Typography>Basis-Strafgebühr:</Typography></Grid>
          <Grid item xs={6}>
            <TextField fullWidth value={formData?.fees.totalPenaltyFeeBasic} onChange={(e) => handleChangePenalty(e, "totalPenaltyFeeBasic")} />
          </Grid>

          <Grid item xs={6}><Typography>👕 T-Shirts:</Typography></Grid>
          <Grid item xs={6}><Typography>{formData?.fees.totalTShirtFee} € </Typography></Grid>

          <Grid item xs={6}><Typography>🍽️ Verpflegung:</Typography></Grid>
          <Grid item xs={6}><Typography>{formData?.fees.totalCateringFee} € </Typography></Grid>

          <Grid item xs={6}><Typography>🌙 Übernachtung:</Typography></Grid>
          <Grid item xs={6}><Typography>{formData?.fees.totalOvernightFee} € </Typography></Grid>

          <Grid item xs={12}><Divider /></Grid>

          <Grid item xs={6}><Typography>Gutschrift (Betrag in €):</Typography></Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={formData?.fees.gutschrift.amount ?? 0}
              onChange={(e) => handleGutschriftChange("amount", e.target.value)}
            />
          </Grid>

          <Grid item xs={6}><Typography>Gutschrift (Beschreibung):</Typography></Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              placeholder="z.B. Treuerabatt, Korrektur, ..."
              value={formData?.fees.gutschrift.description ?? ""}
              onChange={(e) => handleGutschriftChange("description", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          <Grid item xs={6}><Typography><strong>Gesamtbetrag:</strong></Typography></Grid>
          <Grid item xs={6}><Typography><strong>{formData?.fees.totalAmount.toFixed(2)} €</strong></Typography></Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Zahlungshinweise */}
        <Typography variant="h6">🏦 Zahlungshinweise</Typography>
        <TextField fullWidth multiline rows={10} value={competitionData?.paymentInstructions || ""} 
            disabled />

        <Divider sx={{ my: 2 }} />

        {/* Zusätzliche Hinweise */}
        <Typography variant="h6">📝 Zusätzliche Hinweise</Typography>
        <TextField fullWidth multiline rows={10} value={competitionData?.additionalNotes || ""} 
            disabled />

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6">📌 Veranstalter-Anmerkungen</Typography>
        <FormattableTextField
          fullWidth
          multiline
          rows={5}
          value={formData?.customNotes || ""}
          onChange={(e) => setFormData({ ...formData!, customNotes: e.target.value })}
          fieldName="customNotes"
        />

        <Divider sx={{ my: 2 }} />

        {/* Buttons */}
        <Button variant="contained" color="primary" sx={{ mt: 2, mr: 2 }} onClick={handleSave}>
          Speichern
        </Button>
        <Button variant="contained" color="secondary" sx={{ mt: 2 }} onClick={handleSaveAndPDF}>
          Speichern & PDF erstellen
        </Button>
      </Paper>
    </Container>
  );
};

export default RegistrationConfirmationForm;